/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import NotAllowed from 'src/app/permissions/not-allowed';
import Restricted from 'src/app/permissions/restricted';
import { PageTitle } from '../../../_metronic/layout/core';
import StatCard from './components/StatCard';
import ReservationChart from './components/ReservationChart';
import ServiceRequestsList from './components/ServiceRequestsList';
import {
  useOccupancyToday,
  useCheckinsToday,
  useCheckoutsToday,
  useUpcomingReservations,
  useRevenueToday,
  useServiceRequests,
  useInHouseGuests,
} from './service/index';

const DashboardPage: FC = () => {
  const onErrorFetch = (e: any) => e?.response?.data?.message && toast.error(e?.response?.data?.message);

  // API çağrıları
  const { data: occupancyData, isLoading: occupancyLoading } = useOccupancyToday(onErrorFetch);
  const { data: checkinsData, isLoading: checkinsLoading } = useCheckinsToday(onErrorFetch);
  const { data: checkoutsData, isLoading: checkoutsLoading } = useCheckoutsToday(onErrorFetch);
  const { data: upcomingData, isLoading: upcomingLoading } = useUpcomingReservations(onErrorFetch);
  const { data: revenueData, isLoading: revenueLoading } = useRevenueToday(onErrorFetch);
  const { data: serviceRequestsData, isLoading: serviceRequestsLoading } = useServiceRequests(onErrorFetch);
  const { data: inHouseData, isLoading: inHouseLoading } = useInHouseGuests(onErrorFetch);

  return (
    <>
      {/* Üst İstatistik Kartları */}
      <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
        <div className='col-xl-3 col-lg-6 col-md-6 col-sm-12'>
          <StatCard
            color='primary'
            icon='/media/icons/duotone/Home/Building.svg'
            iconColor='white'
            title={occupancyData?.occupancyRate ? `%${occupancyData.occupancyRate}` : '-'}
            description='Doluluk Oranı'
            isLoading={occupancyLoading}
          />
        </div>
        <div className='col-xl-3 col-lg-6 col-md-6 col-sm-12'>
          <StatCard
            color='success'
            icon='/media/icons/duotone/Communication/Group.svg'
            iconColor='white'
            title={inHouseData?.totalGuests ?? '-'}
            description='Oteldeki Misafirler'
            isLoading={inHouseLoading}
          />
        </div>
        <div className='col-xl-3 col-lg-6 col-md-6 col-sm-12'>
          <StatCard
            color='info'
            icon='/media/icons/duotone/Navigation/Sign-in.svg'
            iconColor='white'
            title={checkinsData?.totalCheckins ?? '-'}
            description="Bugün Check-in'ler"
            isLoading={checkinsLoading}
          />
        </div>
        <div className='col-xl-3 col-lg-6 col-md-6 col-sm-12'>
          <StatCard
            color='warning'
            icon='/media/icons/duotone/Navigation/Sign-out.svg'
            iconColor='white'
            title={checkoutsData?.totalCheckouts ?? '-'}
            description="Bugün Check-out'lar"
            isLoading={checkoutsLoading}
          />
        </div>
      </div>

      {/* İkinci Sıra: Gelir ve Servis */}
      <div className='row g-5 g-xl-8 mb-5 mb-xl-8'>
        <div className='col-xl-6 col-lg-6 col-md-6 col-sm-12'>
          <StatCard
            className='h-100'
            color='danger'
            icon='/media/icons/duotone/Shopping/Dollar.svg'
            iconColor='white'
            title={
              revenueData?.today?.revenue !== undefined
                ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(revenueData.today.revenue)
                : '-'
            }
            description='Bugünkü Gelir'
            isLoading={revenueLoading}
          />
        </div>
        <div className='col-xl-6 col-lg-6 col-md-6 col-sm-12'>
          <StatCard
            className='h-100'
            color='dark'
            icon='/media/icons/duotone/Shopping/Settings.svg'
            iconColor='white'
            title={serviceRequestsData?.pending ?? '-'}
            description='Bekleyen Hizmet Talepleri'
            isLoading={serviceRequestsLoading}
          />
        </div>
      </div>

      {/* Grafikler ve Listeler */}
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-6'>
          <ReservationChart chartData={upcomingData} isLoading={upcomingLoading} />
        </div>
        <div className='col-xl-6'>
          <ServiceRequestsList requests={serviceRequestsData?.requests} isLoading={serviceRequestsLoading} />
        </div>
      </div>
    </>
  );
};

const DashboardWrapper: FC = () => {
  const intl = useIntl();

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.DASHBOARD' })}</PageTitle>
      <Restricted to='ROLE_DASHBOARD' fallback={<NotAllowed />}>
        <DashboardPage />
      </Restricted>
    </>
  );
};

export { DashboardWrapper };
